// Zoom, mode-aware: grid mode = column count, rows mode = row
// height. Also owns the cog's Display section (grid <-> justified-rows
// toggle) and two-finger pinch-to-zoom on #grid (steps cols / scales row
// height, anchored under the pinch midpoint; cancels Sortable's pending drag
// on a second touch). `state`, `grid` and `colsLabel` are owned by app.js and
// passed in explicitly.
import { settings } from './settings.js';
import { backfillRatios } from './thumbs.js';
import { cellAt } from './util.js';
import { cancelPendingDrag } from './gestures.js';

const MIN_COLS = 2, MAX_COLS = 20;
const MIN_ROWH = 60, MAX_ROWH = 600, ROWH_STEP = 20;

export function clampCols(n) { return Math.max(MIN_COLS, Math.min(MAX_COLS, n | 0)); }
export function clampRowH(n) { return Math.max(MIN_ROWH, Math.min(MAX_ROWH, n | 0)); }

export function initLayout({ state, grid, colsLabel, settingsPanel }) {

  // Sets everything the active mode needs: --cols, --rowh, the .rows class,
  // the toolbar label, and (rows mode) --ar on any cell whose thumb is
  // already cached — mode just switched, or the same folder reopened —
  // since those won't refire the lazy-load IntersectionObserver that
  // normally sets --ar. Cheap enough to call wholesale on every
  // render/zoom/mode-switch rather than diffing what actually changed.
  function applyLayout() {
    document.documentElement.style.setProperty('--cols', state.cols);
    document.documentElement.style.setProperty('--rowh', state.rowH + 'px');
    grid.classList.toggle('rows', state.displayMode === 'rows');
    if (state.displayMode === 'rows') backfillRatios(grid.children);
    colsLabel.textContent = state.displayMode === 'rows' ? state.rowH + 'px' : state.cols;
  }
  function setCols(n) {
    n = clampCols(n);
    if (n === state.cols) return;
    state.cols = n;
    settings.set('cols', n);
    applyLayout();
  }
  function setRowH(n) {
    n = clampRowH(n);
    if (n === state.rowH) return;
    state.rowH = n;
    settings.set('rowH', n);
    applyLayout();
  }
  function setMode(mode) {
    if (mode === state.displayMode) return;
    state.displayMode = mode;
    settings.set('displayMode', mode);
    applyLayout();
  }
  // zoom IN = bigger content (fewer cols / taller rows), zoom OUT = smaller —
  // same direction in both modes, routed through whichever knob is active.
  const zoomIn = () => state.displayMode === 'rows' ? setRowH(state.rowH + ROWH_STEP) : setCols(state.cols - 1);
  const zoomOut = () => state.displayMode === 'rows' ? setRowH(state.rowH - ROWH_STEP) : setCols(state.cols + 1);

  const section = settingsPanel.addSection('Display');
  settingsPanel.addSelect(section, {
    label: 'Layout', key: 'displayMode',
    options: [['grid', 'Square grid'], ['rows', 'Justified rows']],
  });
  settings.onChange('displayMode', setMode);

  initPinchZoom({ state, grid, setCols, setRowH });

  return { applyLayout, zoomIn, zoomOut };
}

// ---------- Pinch-to-zoom ----------
// Two-finger pinch over the grid retargets whichever knob applyLayout is
// currently driving: cols in grid mode (discrete, hysteresis so hand tremor
// doesn't flap between two counts) or row height in rows mode (continuous —
// there's no natural "step" to hysteresis around). #grid has
// touch-action: pan-y (styles.css) so the browser keeps vertical scroll but
// leaves pinch/horizontal gestures to us — combined with preventDefault
// while 2 pointers are down, this is what keeps page/browser zoom from
// triggering.
//
// A capture-phase *document* pointerdown listener sees the second touch
// pointer before Sortable's own listener (bubble-phase, attached to `grid`,
// see sortable.complete.esm.js's `on()` using non-capture mode) gets a
// chance to treat it as a fresh tap/drag: we stopPropagation() it and also
// cancel whatever delayed-drag Sortable already armed for the first finger via
// gestures.js's cancelPendingDrag() (see there for how the synthetic cancel
// reaches Sortable). No app-visible selection changes.
function initPinchZoom({ state, grid, setCols, setRowH }) {
  const PINCH_STEP = 0.15;   // 15% distance change per column step
  const ptrs = new Map();    // touch pointerId -> {x,y}, only while pinching
  let stepDist = 0, baseRowH = 0, baseDist = 0, anchorCell = null;

  const mid = () => {
    const [a, b] = [...ptrs.values()];
    return { d: Math.hypot(a.x - b.x, a.y - b.y), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };
  // Keep the item under the pinch midpoint at the same screen position: a
  // reflowing grid doesn't scale uniformly (a 6->2 col jump moves items
  // into wildly different rows), so scaling scrollTop by the grid's overall
  // height ratio drifts badly — tracking the actual cell under the fingers
  // and re-scrolling by exactly how far *it* moved is exact instead. The
  // anchor cell is picked once at gesture start (not re-hit-tested on every
  // pointermove, which fires at up to 60Hz) and stays fixed for the whole
  // pinch, same as the finger midpoint it's anchoring to.
  function anchored(mutate) {
    const before = anchorCell && anchorCell.getBoundingClientRect().top;
    mutate();
    if (anchorCell) grid.scrollTop += anchorCell.getBoundingClientRect().top - before;
  }

  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch' || !grid.contains(e.target)) return;
    if (ptrs.size === 1) {
      e.stopPropagation();
      cancelPendingDrag();
    }
    ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ptrs.size === 2) {
      const m = mid();
      baseDist = stepDist = m.d;
      baseRowH = state.rowH;
      anchorCell = cellAt(m.x, m.y);
    }
  }, true);

  document.addEventListener('pointermove', (e) => {
    if (!ptrs.has(e.pointerId)) return;
    ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ptrs.size !== 2) return;
    e.preventDefault();
    const { d } = mid();
    if (state.displayMode === 'rows') {
      const rowH = clampRowH(baseRowH * (d / baseDist));
      if (rowH !== state.rowH) anchored(() => setRowH(rowH));
    } else {
      const ratio = d / stepDist;
      const dir = ratio > 1 + PINCH_STEP ? -1 : ratio < 1 - PINCH_STEP ? 1 : 0;
      if (dir) { anchored(() => setCols(state.cols + dir)); stepDist = d; }
    }
  }, { capture: true, passive: false });

  const endPtr = (e) => { ptrs.delete(e.pointerId); if (ptrs.size < 2) anchorCell = null; };
  document.addEventListener('pointerup', endPtr, true);
  document.addEventListener('pointercancel', endPtr, true);
}
